import { Controller, useForm } from 'react-hook-form';
import { FormControl, InputLabel, NativeSelect, Stack } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { useGetKnowledgeBasesQuery } from 'services/knowledgeBaseApi';
import { useNotification } from 'hook/useNotification';

export function KnowledgeBaseNodeForm({
  id,
  data = {
    knowledgeBaseId: '0',
    label: 'Base de connaissance'
  },
  onUpdateData
}) {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();
  const prevKnowledgeBaseIdRef = useRef(data?.knowledgeBaseId || '0');

  const {
    data: knowledgeBases = [],
    isLoading: knowledgeBasesLoading,
    isError: knowledgeBaseError
  } = useGetKnowledgeBasesQuery();

  const { control, watch } = useForm({
    defaultValues: {
      knowledgeBaseId: data?.knowledgeBaseId || '0'
    }
  });

  const formValues = watch();

  useEffect(() => {
    // Update ref before calling onNodeChange to prevent multiple updates
    const knowledgeBaseIdChanged = formValues.knowledgeBaseId !== prevKnowledgeBaseIdRef.current;
    if (knowledgeBaseIdChanged) {
      prevKnowledgeBaseIdRef.current = formValues.knowledgeBaseId;
      const selectedKB = knowledgeBases.find((kb) => kb.id === formValues.knowledgeBaseId);

      const updatedData = {
        ...data,
        knowledgeBaseId: formValues.knowledgeBaseId,
        label: selectedKB ? selectedKB.label : 'Base de connaissance'
      };

      onUpdateData({
        id,
        data: updatedData
      });
    }
  }, [formValues, data, onUpdateData, id, knowledgeBases]);

  if (knowledgeBaseError) {
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.advisor.fetch.error' })
      });
    } catch {
      // Ignore exception
    }
  }

  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      <Controller
        name="knowledgeBaseId"
        control={control}
        rules={{ required: msg({ id: 'field.knowledgeBase.required' }) }}
        render={({ field }) =>
          knowledgeBasesLoading ? (
            <IndeterminateLoading />
          ) : (
            <FormControl fullWidth>
              <InputLabel variant="standard" id="knowledgeBase-label">
                {msg({ id: 'field.knowledgeBase.label' })}
              </InputLabel>
              <NativeSelect
                {...field}
                label={msg({ id: 'field.knowledgeBase.label' })}
                required
                inputProps={{
                  id: 'knowledgeBase-native-select',
                  name: 'knowledgeBaseId'
                }}
              >
                <option value="0">Aucun</option>
                {knowledgeBases.map((kb) => (
                  <option key={kb.id} value={kb.id}>
                    {`${kb.code} (${kb.label})`}
                  </option>
                ))}
              </NativeSelect>
            </FormControl>
          )
        }
      />
    </Stack>
  );
}

KnowledgeBaseNodeForm.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.string,
    knowledgeBaseId: PropTypes.string,
    label: PropTypes.string
  }),
  onUpdateData: PropTypes.func.isRequired,
  selected: PropTypes.bool
};
